import { hasIconForProvider, type GameProvider } from '@/features/games/data/gameProviders';
import { sortUnavailableGamesLast } from '@/features/games/utils/homeProviderFilters';
import type { HeroGame } from './types';

/** The hero needs at least this many icons; a shorter catalog is repeated up to it. */
export const HERO_GAMES_MIN = 6;
/** Upper bound on the games passed to the hero (the cluster shows the first 7). */
export const HERO_GAMES_MAX = 12;

function isPlayable(game: GameProvider): boolean {
  return game.status !== 'inactive' && game.status !== 'maintenance' && game.depositsOpen !== false;
}

/** The same "Hot" test the Browse grid tile uses (any admin tag shows the Hot badge there). */
function isHot(game: GameProvider): boolean {
  return !!game.clientTag?.trim();
}

/**
 * The games the guest hero shows, from the catalog the Browse grid already loaded (no new
 * requests). Only games with real icon art (the grid's own test, hasIconForProvider). Order:
 * playable games with Hot ones first, then games under maintenance, then Coming soon, each group
 * in catalog order. At most HERO_GAMES_MAX; a catalog with fewer than HERO_GAMES_MIN games with
 * art is repeated in order up to that; no art at all gives [] (the hero shows no visual).
 */
export function pickHeroGames(catalog: readonly GameProvider[]): HeroGame[] {
  const withArt = catalog.filter((game) => !!game.icon && hasIconForProvider(game.code));
  const playable = withArt.filter(isPlayable);
  const ordered = [
    ...playable.filter(isHot),
    ...playable.filter((game) => !isHot(game)),
    ...sortUnavailableGamesLast(withArt.filter((game) => !isPlayable(game))),
  ].slice(0, HERO_GAMES_MAX);
  if (ordered.length === 0) return [];

  const games: HeroGame[] = ordered.map((game) => ({ id: game.id, name: game.name, icon: game.icon, hot: isHot(game) }));
  const out = [...games];
  for (let i = 0; out.length < HERO_GAMES_MIN; i += 1) out.push(games[i % games.length]);
  return out;
}
